import { useContext, useEffect, useRef, useState } from "react";
import AuthContext from "../../../../contexts/AuthContext";
import axios from "axios";
import { Loader, Flex, Text, Grid } from "@mantine/core";
import MantineSwitchButton from "../../../../components/MantineSwitchButton/MantineSwitchButton";
import { IconCheck, IconX } from "@tabler/icons-react";
import showNotification from "../../../../utils/showNotification";

const BOT_INSTANCE_NAME = 'academico-bot'


const getNormalizedId = (idField) => {
    if (!idField) return "";
    if (Array.isArray(idField)) {
        return idField.length === 1 ? String(idField[0]) : `multiple-${idField.length}`;
    }
    return String(idField);
};

const WPGroupAlert = ({ form }) => {
    const { authToken } = useContext(AuthContext);
    const initialState = {
        data: null,
        error: "",
        isLoading: false
    };
    const [state, setState] = useState(initialState);
    const [evolutionInstances, setEvolutionInstances] = useState({
        isLoading: false,
        data: [],
        error: "",
    });

    // Ref para recordar cuál fue la última ID que consultamos exitosamente
    const lastFetchedIdRef = useRef("");

    // 🆕 Ref para rastrear la última consulta de Evolution API
    const lastFetchedEvolutionIdRef = useRef("");

    const isFirstRenderRef = useRef(true);

    const currentUnitIdString = getNormalizedId(form.values.academicUnitId);

    useEffect(() => {
        const getEvolutionApiInstances = async () => {
            setEvolutionInstances((prev) => ({ ...prev, isLoading: true }));
            const URL = `${process.env.REACT_APP_EVOLUTION_API_URL}/instance/fetchInstances`;
            const API_KEY = process.env.REACT_APP_EVOLUTION_API_KEY;

            const options = {
                method: "GET",
                url: URL,
                headers: {
                    apikey: API_KEY,
                },
            };

            axios(options)
                .then((response) => {
                    const { data } = response;

                    const responseData = data
                        .filter((instance) => instance.connectionStatus === 'open')
                        .map((instance) => {
                            const { name, number } = instance;
                            return {
                                value: name,
                                label: number ? `${name} (${number})` : name
                            };
                        });

                    lastFetchedEvolutionIdRef.current = currentUnitIdString;

                    setEvolutionInstances((prev) => ({
                        ...prev,
                        data: responseData,
                    }));
                    form.setFieldValue("api_instances", responseData.map(item => item.value));
                })
                .catch((error) => {
                    // ✅ También actualizamos el ref en caso de error
                    lastFetchedEvolutionIdRef.current = currentUnitIdString;

                    setEvolutionInstances(prev => ({
                        ...prev,
                        error: error.message
                    }))
                    showNotification({
                        color: "red",
                        status: "error",
                        title: "Ha ocurrido un error",
                        autoClose: 6000,
                        message: `No se han cargado bien los bots. Por favor intente nuevamente. Detalle: ${error.message}`,
                    });
                })
                .finally(() => {
                    setEvolutionInstances((prev) => ({
                        ...prev,
                        isLoading: false,
                    }));
                });
        };

        if (currentUnitIdString === lastFetchedEvolutionIdRef.current) {
            return;
        }

        getEvolutionApiInstances();
    }, [currentUnitIdString]);

    useEffect(() => {
        if (currentUnitIdString === lastFetchedIdRef.current) {
            return;
        }

        const getData = async () => {
            if (!currentUnitIdString || currentUnitIdString.startsWith("multiple-")) {
                lastFetchedIdRef.current = currentUnitIdString;
                form.setFieldValue("has_wp_group", false);
                return setState(initialState);
            }

            try {
                setState(prev => ({ ...prev, isLoading: true }));

                const URL = `${process.env.REACT_APP_BACKEND_SERVER}campus/getWpGroupByAcademicUnit/${currentUnitIdString}`;
                const config = {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`
                    }
                };

                const { data } = await axios.get(URL, config);

                lastFetchedIdRef.current = currentUnitIdString;
                setState(prev => ({ ...prev, data }));

                const hasGroup = !!data?.wp_group_id;
                form.setFieldValue("has_wp_group", hasGroup);

                if (isFirstRenderRef.current) {
                    isFirstRenderRef.current = false;
                } else {
                    form.setFieldValue("is_already_in_wp", false);
                }

            } catch (error) {
                lastFetchedIdRef.current = currentUnitIdString;
                setState(prev => ({ ...prev, error: error?.message }));
                form.setFieldValue("has_wp_group", false);
            } finally {
                setState(prev => ({ ...prev, isLoading: false }));
            }
        };

        getData();

    }, [currentUnitIdString, authToken]);

    if (state.isLoading) {
        return (
            <Flex w='100%' align='center' justify='start' my={10}>
                <Loader size="sm" />
            </Flex>
        );
    }

    const exist = state.data?.wp_group_id;
    if (!currentUnitIdString || currentUnitIdString.startsWith("multiple-")) return null;

    const IS_BOT_CLOSED = !evolutionInstances?.data || !evolutionInstances?.data?.find(item => item.value?.toLowerCase() === BOT_INSTANCE_NAME)

    if (IS_BOT_CLOSED) return <Grid.Col span={12}>
        <Flex align='center' justify='start' gap={6} mb={5}>
            <IconX color="red" size={18} />
            <Text size="sm" c={'dimmed'}>
                El teléfono de académico esta desconectado o tiene algun error. Por favor contacta a soporte para resolverlo.
            </Text>
        </Flex>
    </Grid.Col>

    return (
        <>
            <Grid.Col span={12}>
                <Flex align='center' justify='start' gap={6} mb={5}>
                    {exist ? <IconCheck color="green" size={18} /> : <IconX color="red" size={18} />}
                    <Text size="sm" c={exist ? "green" : "dimmed"}>
                        {exist ? "Unidad con grupo de WhatsApp automatizado" : "Unidad antigua (Manejo de WhatsApp Manual)"}
                    </Text>
                </Flex>
            </Grid.Col>

            <Grid.Col span={12}>
                <MantineSwitchButton
                    checked={form.values.is_already_in_wp}
                    onChange={() =>
                        form.setFieldValue("is_already_in_wp", !form.values.is_already_in_wp)
                    }
                    label={
                        exist
                            ? <span>¿Deseas <strong>agregar automáticamente</strong> al alumno al grupo de WP?</span>
                            : <span>¿El alumno ya fue agregado <strong>manualmente</strong> al grupo?</span>
                    }
                />
            </Grid.Col>
        </>
    );
};

export default WPGroupAlert;